import React from 'react';
import { motion } from 'framer-motion';
import { BarChart3, Link as LinkIcon } from 'lucide-react';
import { ScansOverTimeChart } from '../../../../components/charts/ScansOverTimeChart';
import { DeviceBreakdownChart } from '../../../../components/charts/DeviceBreakdownChart';
import { BrowserBreakdownChart } from '../../../../components/charts/BrowserBreakdownChart';
import { PeakHoursChart } from '../../../../components/charts/PeakHoursChart';
import { CountryBreakdownChart } from '../../../../components/charts/CountryBreakdownChart';
import { AnalyticsSummary } from '../types';

interface AnalyticsChartsProps {
  analytics: AnalyticsSummary | undefined;
  isLoading: boolean;
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 15 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' } }
};

// Image-destination scans record the resolved public GET /i/{storage_key} URL as matched_url
// (see resolve_image_public_url in backend/app/api/redirect.py) — recognizable by its /i/ path.
function isImageDestinationUrl(url: string): boolean {
  try {
    return new URL(url).pathname.startsWith('/i/');
  } catch {
    return false;
  }
}

export function AnalyticsCharts({ analytics, isLoading }: AnalyticsChartsProps) {
  const totalScans = analytics?.total_scans || 0;

  // No analytics state
  if (totalScans === 0 && !isLoading) {
    return (
      <div className="bg-white rounded-xl p-6 border border-[#E5DED6] mb-5 text-center">
        <div className="flex flex-col items-center justify-center py-6">
          <div className="w-14 h-14 bg-[#F5F0EB] rounded-full flex items-center justify-center mb-3">
            <BarChart3 className="w-7 h-7 text-[#9A8F85]" />
          </div>
          <h3 className="text-base font-semibold text-[#2D2520] mb-1.5">No Analytics Yet</h3>
          <p className="text-sm text-[#5C524A] max-w-md">
            Analytics will appear here once people start scanning your QR code.
          </p>
        </div>
      </div>
    );
  }

  const LoadingSkeleton = () => (
    <div className="flex items-center justify-center h-48">
      <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-[--clinqr-primary]"></div>
    </div>
  );

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 md:gap-4 mb-5"
    >
      {/* Scans Over Time */}
      <motion.div variants={itemVariants} className="bg-white rounded-xl p-4 border border-[--clinqr-border]/10 shadow-sm">
        {isLoading ? <LoadingSkeleton /> : <ScansOverTimeChart data={analytics?.daily_scans || []} />}
      </motion.div>

      {/* Country Breakdown */}
      <motion.div variants={itemVariants} className="bg-white rounded-xl p-4 border border-[--clinqr-border]/10 shadow-sm">
        {isLoading ? <LoadingSkeleton /> : (
          <CountryBreakdownChart data={analytics?.country_breakdown || []} totalScans={totalScans} />
        )}
      </motion.div>

      {/* Browser Breakdown */}
      <motion.div variants={itemVariants} className="bg-white rounded-xl p-4 border border-[--clinqr-border]/10 shadow-sm">
        {isLoading ? <LoadingSkeleton /> : (
          <BrowserBreakdownChart data={analytics?.browser_breakdown || []} totalScans={totalScans} />
        )}
      </motion.div>

      {/* Peak Hours */}
      <motion.div variants={itemVariants} className="bg-white rounded-xl p-4 border border-[--clinqr-border]/10 shadow-sm">
        {isLoading ? <LoadingSkeleton /> : (
          <PeakHoursChart data={analytics?.hourly_breakdown} totalScans={totalScans} />
        )}
      </motion.div>

      {/* Device Breakdown */}
      <motion.div variants={itemVariants} className="bg-white rounded-xl p-4 border border-[--clinqr-border]/10 shadow-sm">
        {isLoading ? <LoadingSkeleton /> : (
          <DeviceBreakdownChart data={analytics?.device_breakdown || []} totalScans={totalScans} />
        )}
      </motion.div>

      {/* URL Breakdown - For rules-based QR codes */}
      {analytics?.url_breakdown && analytics.url_breakdown.length > 0 && (
        <motion.div variants={itemVariants} className="bg-white rounded-xl p-4 border border-[--clinqr-border]/10 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-[--clinqr-text-primary] flex items-center gap-2">
              <LinkIcon className="w-4 h-4 text-[--clinqr-primary]" />
              URL Performance
            </h3>
          </div>
          <p className="text-xs text-[--clinqr-text-secondary] mb-3">Which URLs are being served</p>
          <div className="space-y-2">
            {analytics.url_breakdown.slice(0, 5).map((item, index) => {
              const percentage = totalScans > 0 ? ((item.count / totalScans) * 100).toFixed(1) : '0';
              const isImage = isImageDestinationUrl(item.url);
              return (
                <div key={index} className="flex items-center gap-2">
                  {isImage && (
                    <img
                      src={item.url}
                      alt="Destination"
                      className="w-6 h-6 object-cover rounded border border-[--clinqr-border]/20 flex-shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      {isImage ? (
                        <a
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-[--clinqr-primary] hover:underline font-medium"
                          title={item.url}
                        >
                          Image
                        </a>
                      ) : (
                        <a
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-[--clinqr-primary] hover:underline truncate max-w-[60%] font-medium"
                          title={item.url}
                        >
                          {item.url}
                        </a>
                      )}
                      <span className="text-xs font-medium text-[--clinqr-text-primary]">
                        {item.count} ({percentage}%)
                      </span>
                    </div>
                    <div className="w-full bg-[--clinqr-bg-subtle] rounded-full h-1.5">
                      <div
                        className="bg-[--clinqr-primary] h-1.5 rounded-full transition-all duration-500"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}
